import { Command, arrayLast } from "@rauricoste/core";
import {
  UndoCommand,
  UndoState,
  emptyUndoCommandApi,
  undoApiFactory,
} from "./undoApi";
import { Reducer, StateWithHistory } from "../../alias";
import { GameCommandByPlayer } from "./gameReducerFactory";

export type StateWithUndo<State, Command> = StateWithHistory<
  { gameState: State; undoState: UndoState },
  GameCommandByPlayer<Command | UndoCommand>
>;

const revertCommands = (
  undoState: UndoState
): { undoState: UndoState; isReverted: boolean } => {
  const { playerIds, answers } = undoState;
  const isReverted = playerIds.every((playerId) => answers[playerId] === true);
  const isRefused = playerIds.some((playerId) => answers[playerId] === false);
  return {
    undoState: isReverted
      ? { playerIds, answers: {}, justReverted: true }
      : isRefused
      ? { playerIds, answers: {}, justReverted: false }
      : undoState,
    isReverted,
  };
};

export const buildReducerWithUndo = <State, Command>(
  stateReducer: Reducer<State, GameCommandByPlayer<Command>>
): Reducer<
  StateWithUndo<State, Command>,
  GameCommandByPlayer<Command | UndoCommand>
> => {
  return (
    stateWithUndo: StateWithUndo<State, Command>,
    command: GameCommandByPlayer<Command | UndoCommand>
  ) => {
    const { initState, steps } = stateWithUndo;
    if (Command.isCommand(command, emptyUndoCommandApi)) {
      const lastStep = arrayLast(steps);
      if (!lastStep) {
        throw new Error("no command to undo");
      }
      const undoState2 = lastStep.state.undoState;
      const undoState3 = Command.call(
        undoApiFactory(undoState2, command.playerId),
        command
      );
      const { undoState: undoState4, isReverted } = revertCommands(undoState3);
      let newSteps = [...steps];
      if (isReverted) {
        const lastStepNoUndo = [...steps]
          .reverse()
          .find(
            (step) => !Command.isCommand(step.command, emptyUndoCommandApi)
          );
        if (lastStepNoUndo) {
          const stepIndex = steps.indexOf(lastStepNoUndo);
          newSteps = steps.slice(0, stepIndex);
        } else {
          newSteps = [];
        }
      } else {
        newSteps.push({
          state: {
            gameState: lastStep.state.gameState,
            undoState: undoState4,
          },
          command,
        });
      }

      return {
        initState,
        steps: newSteps,
      };
    } else {
      const previousState = arrayLast(steps)?.state ?? initState;
      return {
        initState,
        steps: [
          ...steps,
          {
            state: {
              gameState: stateReducer(previousState.gameState, command),
              undoState: previousState.undoState,
            },
            command,
          },
        ],
      };
    }
  };
};
